import {
  CollectionKind,
  DisplayMode,
  type CollectionOut,
} from '@/api/generated/schemas';

/** Offered order — the default first, then the two that change the layout.
 * `Object.values(DisplayMode)` happens to lead with `banner`. */
export const DISPLAY_MODES: readonly DisplayMode[] = [
  DisplayMode.regular,
  DisplayMode.special,
  DisplayMode.banner,
];

/**
 * Why a section has no mode to choose. The server decides both cases on its
 * own, so offering a control would be a lie.
 */
export type SectionModeLock =
  /** The auto-created "All" row: the server rewrites its mode to regular. */
  | 'self'
  /** Banner collections build their own carousel and never read the mode. */
  | 'banner_kind';

/**
 * A picked mode that will not reach the app as picked. Advisory: the server
 * degrades rather than rejecting, so this never blocks a save.
 */
export type SectionDisplayIssue =
  /** Banner mode has no image to draw, so the app falls back to regular. */
  'banner_without_image';

/**
 * A row pointing the category at itself is the auto-created "All" section.
 * Unknown while the category has no id yet, i.e. on create.
 */
export const isSelfSection = (
  collectionId: number | null | undefined,
  categoryId: number | null | undefined
): boolean => collectionId != null && collectionId === categoryId;

/** Null while the lookup is still in flight — the row settles into its lock
 * once the collection lands, rather than flashing a wrong reason. */
export const sectionModeLock = (
  collectionId: number | null | undefined,
  categoryId: number | null | undefined,
  collection: CollectionOut | undefined
): SectionModeLock | null => {
  if (isSelfSection(collectionId, categoryId)) return 'self';
  if (collection?.collection_kind === CollectionKind.banner)
    return 'banner_kind';
  return null;
};

/** An unresolved collection says nothing — the row already flags itself as
 * deleted, and guessing here would stack a second complaint on it. */
export const sectionDisplayIssues = (
  mode: DisplayMode | null | undefined,
  collection: CollectionOut | undefined
): SectionDisplayIssue[] =>
  collection && mode === DisplayMode.banner && !collection.banner_url
    ? ['banner_without_image']
    : [];

/**
 * What the app will actually draw, after the server's own degradations — so
 * the preview never promises a layout the feed will not render.
 */
export const effectiveDisplayMode = (
  mode: DisplayMode | null | undefined,
  collectionId: number | null | undefined,
  categoryId: number | null | undefined,
  collection: CollectionOut | undefined
): DisplayMode =>
  mode == null ||
  sectionModeLock(collectionId, categoryId, collection) != null ||
  sectionDisplayIssues(mode, collection).length > 0
    ? DisplayMode.regular
    : mode;
